import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { Icon } from "@iconify/react";
import Datetime from "react-datetime";
import moment from 'moment';

export default function AddAppMenu({ open, onClose, category, eventId, subMenuId, subSectionId }) {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const [imagePreview, setImagePreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [speakers, setSpeakers] = useState([]);
    const modalRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open, onClose]);

    const handleImagePreview = (e, setFieldValue, fieldName) => {
        const file = e.currentTarget.files[0];
        setFieldValue(fieldName, file);
        setImagePreview(file ? URL.createObjectURL(file) : null);
    };

    const fetchSpeakers = async () => {
        try {
            const response = await axios.get(`${apiUrl}event-service/app-event-speaker`, {
                params: { event: eventId },
                headers: { Authorization: `Bearer ${bearerToken}` },
            });
            if (response.data?.success && response.data?.data) {
                setSpeakers(response.data.data);
            } else {
                throw new Error('Failed to fetch speakers');
            }
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
            return [];
        }
    }

    useEffect(() => {
        fetchSpeakers();
    }, [])

    const fetchAppMenu = async () => {
        if (!category) return;

        const endpointMap = {
            schedule: `event-service/app-schedule/${subMenuId}`,
            exhibitors: `event-service/app-exhibitor/${subMenuId}`,
            sponsors: `event-service/app-sponsor/${subMenuId}`,
        };

        const key = category.trim().toLowerCase();
        if (!endpointMap[key]) {
            console.warn(`No API endpoint found for category: ${category}`);
            return;
        }

        try {
            const response = await axios.get(`${apiUrl}${endpointMap[key]}`, {
                headers: { Authorization: `Bearer ${bearerToken}` },
            });

            if (!response.data?.success || !response.data?.data) {
                throw new Error(`Failed to fetch ${category}`);
            }

            const data = response.data.data;

            // Map API data → Formik's values
            switch (key) {
                case "schedule":
                    formik.setValues({
                        name: data.name || "",
                        location: data.location || "",
                        description: data.description || "",
                        date: data.date ? moment(data.date) : null,
                        startTime: data.startTime ? moment(data.startTime, "HH:mm:ss") : "",
                        endTime: data.endTime ? moment(data.endTime, "HH:mm:ss") : "",
                        prerequisite: Array.isArray(data.prerequisite) ? data.prerequisite.join(", ") : (data.prerequisite || ""),
                        whatToBring: Array.isArray(data.whatToBring) ? data.whatToBring.join(", ") : (data.whatToBring || ""),
                        tags: Array.isArray(data.tags) ? data.tags.join(", ") : (data.tags || ""),
                        speakerId: data.speakerId || "",
                    });
                    break;
                case "exhibitors":
                    formik.setValues({
                        name: data.name || "",
                        booth: data.booth || "",
                        description: data.description || "",
                        image: "",
                    });
                    setImagePreview(data.image || null);
                    break;
                case "sponsors":
                    formik.setValues({
                        name: data.name || "",
                        service: data.service || "",
                        description: data.description || "",
                        profilePic: "",
                    });
                    setImagePreview(data.profilePic || null);
                    break;
                default:
                    break;
            }

        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        }
    };

    useEffect(() => {
        if (open && category && subMenuId) {
            fetchAppMenu();
        }
    }, [category, subMenuId, open]);

    const buildUrl = (baseUrl) => `${apiUrl}${baseUrl}${subMenuId ? `/${subMenuId}` : ""}`;

    const formatLabel = (field) =>
        field
            .replace(/([A-Z])/g, " $1") // insert space before capital letters
            .replace(/^./, (str) => str.toUpperCase()); // capitalize first letter


    const formConfigs = {
        schedule: {
            initialValues: { name: '', description: '', location: '', prerequisite: '', whatToBring: '', tags: '', speakerId: '', date: '', startTime: '', endTime: '' },
            validationSchema: Yup.object({
                name: Yup.string().required('Required'),
            }),
            onSubmit: async (values) => {
                // Transform fields to match API spec
                const payload = {
                    name: values.name,
                    category: subSectionId,
                    description: values.description,
                    location: values.location,
                    date: values.date
                        ? moment(values.date).format("YYYY-MM-DD")
                        : null,
                    startTime: values.startTime
                        ? moment(values.startTime).format("HH:mm:ss")
                        : null,
                    endTime: values.endTime
                        ? moment(values.endTime).format("HH:mm:ss")
                        : null,
                    prerequisite: values.prerequisite
                        ? values.prerequisite.split(",").map(s => s.trim()) // convert comma-separated to array
                        : [],
                    whatToBring: values.whatToBring
                        ? values.whatToBring.split(",").map(s => s.trim())
                        : [],
                    tags: values.tags
                        ? values.tags.split(",").map(s => s.trim())
                        : [],
                    speakerId: values.speakerId,
                    eventId: eventId,
                };
                Object.keys(payload).forEach(key => {
                    if (
                        payload[key] === null ||
                        payload[key] === "" ||
                        (Array.isArray(payload[key]) && payload[key].length === 0)
                    ) {
                        delete payload[key];
                    }
                });

                await axios({
                    method: subMenuId ? "put" : "post",
                    url: buildUrl("event-service/app-schedule"),
                    data: payload,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                        "Content-Type": "application/json",
                        accept: "application/json",
                    },
                });
            },
            render: ({ values, errors, touched, submitCount, handleChange, handleBlur, setFieldValue }) => (
                <>
                    {['name', 'location', 'prerequisite', 'whatToBring', 'tags'].map((field) => (
                        <div key={field} className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {formatLabel(field)} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name={field}
                                placeholder={formatLabel(field)}
                                value={values[field]}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                            {(touched[field] || submitCount > 0) && errors[field] && <div className="text-red-500 text-xs">{errors[field]}</div>}
                        </div>
                    ))}
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Speaker
                        </label>
                        <select
                            name="speakerId"
                            value={values.speakerId}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                        >
                            <option value="" disabled>
                                Select Speakers
                            </option>
                            {speakers.map((speaker) => (
                                <option key={speaker.id} value={speaker.id}>
                                    {speaker.name}
                                </option>
                            ))}
                        </select>
                        {(touched.speakerId || submitCount > 0) && errors.speakerId && <div className="text-red-500">{errors.speakerId}</div>}
                    </div>
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            name="description"
                            value={values.description}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        {(touched.description || submitCount > 0) && errors.description && (
                            <div className="text-red-500 text-xs">{errors.description}</div>
                        )}
                    </div>
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date <span className="text-red-500">*</span></label>
                        <Datetime
                            name="date"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            value={values.date}
                            onChange={(val) => setFieldValue('date', val)}
                            onBlur={handleBlur}
                            timeFormat={false}
                            dateFormat="YYYY/MM/DD"
                            closeOnSelect={true}
                        />
                        {touched.date && errors.date && (
                            <div className="text-red-500 text-sm">{errors.date}</div>
                        )}
                    </div>
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start Time <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Datetime
                                value={values.startTime}
                                onChange={(val) => setFieldValue('startTime', val)}
                                onBlur={handleBlur}
                                dateFormat={false}
                                timeFormat="hh:mm A"
                                closeOnSelect={true}
                            />
                            <Icon icon="bitcoin-icons:clock-outline" className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        </div>
                        {errors.startTime && (
                            <div className="text-red-500 text-sm mt-1">
                                {errors.startTime}
                            </div>
                        )}
                    </div>
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            End Time <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Datetime
                                value={values.endTime}
                                onChange={(val) => setFieldValue('endTime', val)}
                                onBlur={handleBlur}
                                dateFormat={false}
                                timeFormat="hh:mm A"
                                isValidDate={(currentTime) => {
                                    const startTime = values.startTime;
                                    return startTime ? currentTime.isAfter(startTime) : true;
                                }}
                                closeOnSelect={true}
                            />
                            <Icon icon="bitcoin-icons:clock-outline" className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        </div>
                        {errors.endTime && (
                            <div className="text-red-500 text-sm mt-1">
                                {errors.endTime}
                            </div>
                        )}
                    </div>
                </>
            ),
        },

        exhibitors: {
            initialValues: { name: '' },
            validationSchema: Yup.object({ name: Yup.string().required('Required') }),
            onSubmit: async (values) => {
                const params = new FormData();
                params.append("eventId", eventId);
                params.append("category", subSectionId)
                Object.entries(values).forEach(([k, v]) => params.append(k, v));

                await axios({
                    method: subMenuId ? "put" : "post",
                    url: buildUrl('event-service/app-exhibitor'),
                    data: params,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                        "Content-Type": "multipart/form-data",
                        accept: "application/json",
                    },
                });
            },
            render: ({ values, errors, touched, submitCount, handleChange, handleBlur, setFieldValue }) => (
                <>
                    {['name', 'booth'].map((field) => (
                        <div key={field} className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {field.charAt(0).toUpperCase() + field.slice(1)} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name={field}
                                placeholder={field}
                                value={values[field]}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                            {(touched[field] || submitCount > 0) && errors[field] && <div className="text-red-500 text-xs">{errors[field]}</div>}
                        </div>
                    ))}
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            name="description"
                            value={values.description}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        {(touched.description || submitCount > 0) && errors.description && (
                            <div className="text-red-500 text-xs">{errors.description}</div>
                        )}
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center relative">
                            {imagePreview ? (
                                <>
                                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-lg" />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setImagePreview(null);
                                            setFieldValue('image', null);
                                        }}
                                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center"
                                    >
                                        <Icon icon="material-symbols-light:close-rounded" />
                                    </button>
                                </>
                            ) : (
                                <svg
                                    className="w-6 h-6 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            )}
                        </div>
                        <label className="cursor-pointer">
                            <span className="text-blue-500 border border-blue-500 rounded-md px-4 py-1">
                                {imagePreview ? 'Change Image' : 'Choose Image'}
                            </span>
                            <input
                                type="file"
                                name="image"
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => handleImagePreview(e, setFieldValue, 'image')}
                            />
                        </label>
                    </div>
                    {(touched.image || submitCount > 0) && errors.image && <div className="text-red-500 text-xs">{errors.image}</div>}
                </>
            ),
        },

        sponsors: {
            initialValues: { name: '' },
            validationSchema: Yup.object({ name: Yup.string().required('Required') }),
            onSubmit: async (values) => {
                const params = new FormData();
                params.append("eventId", eventId);
                params.append("category", subSectionId)
                Object.entries(values).forEach(([k, v]) => params.append(k, v));

                await axios({
                    method: subMenuId ? "put" : "post",
                    url: buildUrl('event-service/app-sponsor'),
                    data: params,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                        "Content-Type": "multipart/form-data",
                        accept: "application/json",
                    },
                });
            },
            render: ({ values, errors, touched, submitCount, handleChange, handleBlur, setFieldValue }) => (
                <>
                    {['name', 'service'].map((field) => (
                        <div key={field} className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {field.charAt(0).toUpperCase() + field.slice(1)} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name={field}
                                placeholder={field}
                                value={values[field]}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                            {(touched[field] || submitCount > 0) && errors[field] && <div className="text-red-500 text-xs">{errors[field]}</div>}
                        </div>
                    ))}
                    <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            name="description"
                            value={values.description}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                        {(touched.description || submitCount > 0) && errors.description && (
                            <div className="text-red-500 text-xs">{errors.description}</div>
                        )}
                    </div>

                    <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center relative">
                            {imagePreview ? (
                                <>
                                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-lg" />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setImagePreview(null);
                                            setFieldValue('profilePic', null);
                                        }}
                                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center"
                                    >
                                        <Icon icon="material-symbols-light:close-rounded" />
                                    </button>
                                </>
                            ) : (
                                <svg
                                    className="w-6 h-6 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            )}
                        </div>
                        <label className="cursor-pointer">
                            <span className="text-blue-500 border border-blue-500 rounded-md px-4 py-1">
                                {imagePreview ? 'Change Image' : 'Choose Image'}
                            </span>
                            <input
                                type="file"
                                name="profilePic"
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => handleImagePreview(e, setFieldValue, 'profilePic')}
                            />
                        </label>
                    </div>
                    {(touched.profilePic || submitCount > 0) && errors.profilePic && <div className="text-red-500 text-xs">{errors.profilePic}</div>}
                </>
            ),
        },

        venueMaps: {
            initialValues: { name: '' },
            validationSchema: Yup.object({ name: Yup.string().required('Required') }),
            onSubmit: (values) => submitFormData('event-service/venue-maps', values),
            render: ({ values, errors, touched, handleChange, handleBlur }) => (
                <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Section Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="name"
                        placeholder="Name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                    {touched.name && errors.name && <div className="text-red-500 text-xs">{errors.name}</div>}
                </>
            ),
        },
    };

    const safeConfig = formConfigs[category?.trim()?.toLowerCase()] || {
        initialValues: {},
        validationSchema: Yup.object({}),
        onSubmit: () => { },
        render: () => null,
    };

    const formik = useFormik({
        initialValues: safeConfig.initialValues,
        validationSchema: safeConfig.validationSchema,
        validateOnBlur: true,
        validateOnChange: false,
        enableReinitialize: true,
        onSubmit: async (values, { resetForm }) => {
            setIsLoading(true);
            try {
                await safeConfig.onSubmit(values);
                resetForm();
                onClose();
                setImagePreview(null);
            } catch (err) {
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        },
    });

    if (!open) return null;

    const handleBackdropClick = () => {
        formik.resetForm();
        setImagePreview(null);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            {/* Background overlay */}
            <div className="absolute inset-0 bg-black opacity-50" onClick={handleBackdropClick}></div>

            {/* Modal */}
            <div className="bg-white rounded-lg w-full max-w-xl z-50 relative">
                {/* Header */}
                <div className="bg-blue-500 text-white py-3 px-6 rounded-lg w-3/4 mx-auto relative z-10 -mt-6">
                    <h2 className="text-xl font-semibold text-center">{subMenuId ? 'Edit' : 'Add New'} {category.trim().charAt(0).toUpperCase() + category.trim().slice(1).toLowerCase()}</h2>
                </div>

                <div className="relative bg-white p-6 space-y-6 shadow-md rounded-b-lg overflow-y-auto max-h-[70vh]">
                    <form onSubmit={formik.handleSubmit}>
                        {safeConfig.render({ ...formik, imagePreview, setImagePreview })}
                        <div className="flex justify-end">
                            <button type="submit" disabled={isLoading} className="bg-blue-500 text-white mt-4 px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500">
                                {subMenuId
                                    ? (isLoading ? "Updating..." : "Update")
                                    : (isLoading ? "Saving..." : "Save")
                                }
                                {/* {isLoading ? "Processing..." : subMenuId ? "Update" : "Save"} */}
                                {/* {isLoading? "Saving" : 'Save'} */}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

        </div>
    );
}

AddAppMenu.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    eventId: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    subMenuId: PropTypes.string,
    subSectionId: PropTypes.string,
};